import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "pagenetion-pages" }
const _hoisted_2 = ["onClick"]

import { useWindowSize } from "@vueuse/core";
import {
  ref,
  onMounted,
  watch,
  computed,
} from "vue";

// ---- interface / types ---------------------------------------
export interface Result {
  isSuccess: boolean;
  data: any;
}
interface Props {
  getTotalCount: (cond: any) => Promise<any>; // 全件数取得
  getPageData: (page: number, size: number, cond: any) => Promise<any>; // データ取得
  cond: any; // APIに渡す検索条件
  page?: number; // ページ番号
  size?: number; // 1ページの項目数
  position?: "left" | "center" | "right"; // 位置指定
  refresh?: number;
}
// ---- Props -------------------------------------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'Pagination',
  props: {
    getTotalCount: {},
    getPageData: {},
    cond: {},
    page: { default: 1 },
    size: { default: 10 },
    position: { default: "left" },
    refresh: { default: 0 }
  },
  emits: ["notify"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
// ---- Variable ----------------------------------------------
const emit = __emit;
let count = ref<number>(0);
const totalItems = ref<number>(0);
const currentPage = ref<number>(props.page);
const totalPages = ref<number>(0);
const pages = ref<any>();
const selectedSize = ref<number>(props.size);
const cond = ref<any>(props.cond);
const displayedPages = ref([]);
// watchによる監視を初期表示の段階では無効化しておく
// ※複数データ取得APIが実行されることへの対策
let watchActive = false;
// 画面サイズ
const { width } = useWindowSize();
// 位置クラスを計算
const positionClass = computed(() => {
  switch (props.position) {
    case "left":
      return "justify-left";
    case "right":
      return "justify-right";
    default:
      return "justify-center";
  }
});

// 表示するページ
// 計12ページ、現在が4ページ目の場合
//  表示例. 1 ...2 3 4 5 6  ...12
const pcFilteredDisplayedPages = () => {
  if (totalPages.value <= 10) {
    return pages.value;
  }

  const startPages = pages.value.slice(0, 1);
  const endPages = pages.value.slice(-1);
  const middlePages =
    currentPage.value > 2
      ? pages.value.slice(currentPage.value - 3, currentPage.value + 2)
      : pages.value.slice(0, currentPage.value + 2);

  const displayPages = [...startPages, ...middlePages, ...endPages];
  const uniquePages = [...new Set(displayPages)]; // 重複除外
  const filteredPages = [];

  // 不連続は「...」とする
  for (let i = 0; i < uniquePages.length; i++) {
    if (i > 0 && uniquePages[i] !== uniquePages[i - 1] + 1) {
      filteredPages.push("...");
    }
    filteredPages.push(uniquePages[i]);
  }
  return filteredPages;
};

// 計10ページ、現在が6ページ目の場合
//  表示例. 6 7 ... 10
const spFilteredDisplayedPages = () => {
  if (totalPages.value <= 3) {
    return pages.value;
  }
  // 現在のページと合計ページの差が1の場合
  if (totalPages.value - currentPage.value === 1) {
    return [currentPage.value - 1, currentPage.value, "...", totalPages.value];
  }
  // 最後のページの場合
  else if (currentPage.value === totalPages.value) {
    return [
      currentPage.value - 2,
      currentPage.value - 1,
      "...",
      currentPage.value,
    ];
  }
  // 現在のページと合計ページの差が2以上の場合
  return [currentPage.value, currentPage.value + 1, "...", totalPages.value];
};

const getCurrentDisplayPages = (width: number) => {
  if (width >= 1121) {
    // 1121px以上をPCとして扱う
    return pcFilteredDisplayedPages();
  }
  return spFilteredDisplayedPages();
};

// ---- Method -----------------------------------------------
// 前へ
const prevPage = async () => {
  await update(); // ページ増減の可能性があるため更新
  if (currentPage.value > 1) {
    const result = await getPageData(currentPage.value - 1, selectedSize.value);
    if (result.isSuccess) {
      if (result.data.length > 0) {
        currentPage.value--;
        emit("notify", result);
      } else {
        // データが削除された等の理由で指定されたページがない
        await update();
      }
    } else {
      // エラー
      emit("notify", result);
    }
  }
};
// 次へ
const nextPage = async () => {
  await update(); // ページ増減の可能性があるため更新
  if (currentPage.value < totalPages.value) {
    const result = await getPageData(currentPage.value + 1, selectedSize.value);
    if (result.isSuccess) {
      if (result.data.length > 0) {
        currentPage.value++;
        emit("notify", result);
      } else {
        // データが削除された等の理由で指定されたページがない
        await update();
      }
    } else {
      // エラー
      emit("notify", result);
    }
  }
};
// 指定ページへ
const goToPage = async (page: number) => {
  await update(); // ページ増減の可能性があるため更新
  if (page <= totalPages.value) {
    const result = await getPageData(page, selectedSize.value);
    if (result.isSuccess) {
      if (result.data.length > 0) {
        currentPage.value = page;
        emit("notify", result);
      } else {
        // データが削除された等の理由で指定されたページにデータがない
        await update();
      }
    } else {
      // エラー
      emit("notify", result);
    }
  }
};
// トータル件数更新
const update = async () => {
  await props.getTotalCount(cond.value).then(([model, _response, err]) => {
    if (err) {
      emit("notify", { isSuccess: false, data: err });
    } else {
      totalItems.value = count.value = model.attributes.count;
      // 件数情報をnotifyイベントに含める
      emit("notify", {
        isSuccess: true,
        data: null,
        count: model.attributes.count,
      });
    }
  });
};
// データ取得
const getPageData = async (page: number, size: number): Promise<Result> => {
  return await props
    .getPageData(page, size, cond.value)
    .then(([model, _response, err]) => {
      const result: Result = { isSuccess: true, data: null };
      if (err) {
        result.isSuccess = false;
        result.data = err;
      } else {
        result.data = model.attributes;
      }
      return result;
    });
};
// リフレッシュ
const refresh = async (page: number = currentPage.value) => {
  await update();
  const result = await getPageData(currentPage.value, selectedSize.value);
  emit("notify", result);
};

// ---- Vue hooks -----------------------------------------
onMounted(async () => {
  await refresh();
  watchActive = true;
  displayedPages.value = getCurrentDisplayPages(width.value);
});

watch(width, (newWidth) => {
  displayedPages.value = getCurrentDisplayPages(newWidth);
});

watch(
  [totalItems, selectedSize],
  ([newTotalItems, newSelectedSize]) => {
    totalPages.value = Math.ceil(newTotalItems / newSelectedSize);
    if (newTotalItems === 0) {
      // データ0件の場合は次回検索でデータが取れる可能性があるため初期値に戻す
      currentPage.value = 1;
    } else if (currentPage.value > totalPages.value) {
      // ページ減に伴う現在ページの調整をする
      currentPage.value = totalPages.value;
    }
    pages.value = Array.from({ length: totalPages.value }, (_, i) => i + 1);
  },
  { deep: true, immediate: true }
);
watch(
  [currentPage, totalItems],
  () => {
    displayedPages.value = getCurrentDisplayPages(width.value);
  },
  { deep: true, immediate: true }
);
// propsの変更を監視
watch(
  () => props.cond,
  async (newCond) => {
    cond.value = newCond;
    if (watchActive) await refresh();
  },
  { deep: true, immediate: true }
);
watch(
  () => props.page,
  async (newPage) => {
    currentPage.value = newPage;
    if (watchActive) await refresh();
  },
  { deep: true, immediate: true }
);
watch(
  () => props.refresh,
  async () => {
    if (watchActive) await refresh();
  },
  { deep: true, immediate: true }
);
watch(
  () => props.size,
  async (newSize) => {
    selectedSize.value = newSize;
    if (watchActive) await refresh();
  },
  { deep: true, immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["main-contents-pagenation", positionClass.value])
  }, [
    _createElementVNode("ul", _hoisted_1, [
      _createElementVNode("li", {
        class: _normalizeClass(['pagenetion-icon', { selectable: currentPage.value > 1 }]),
        onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (currentPage.value > 1 && prevPage()), ["stop"]))
      }, _cache[2] || (_cache[2] = [
        _createElementVNode("img", {
          src: "/server/necs/images/arrow-right_pale_icon.png",
          class: "pagenetion-back"
        }, null, -1)
      ]), 2),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedPages.value, (page) => {
        return (_openBlock(), _createElementBlock("li", {
          key: page,
          class: _normalizeClass({
          selected: page === currentPage.value,
          selectable: page !== '...',
          'pagenetion-number': page !== '...',
          'pagenetion-unselectable': page === '...',
        }),
          onClick: _withModifiers(($event: any) => (page !== '...' && goToPage(page)), ["stop"])
        }, _toDisplayString(page), 11, _hoisted_2))
      }), 128)),
      _createElementVNode("li", {
        class: _normalizeClass(['pagenetion-icon', { selectable: currentPage.value < totalPages.value }]),
        onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (currentPage.value < totalPages.value && nextPage()), ["stop"]))
      }, _cache[3] || (_cache[3] = [
        _createElementVNode("img", {
          src: "/server/necs/images/arrow-right_pale_icon.png",
          class: "pagenetion-next"
        }, null, -1)
      ]), 2)
    ])
  ], 2))
}
}

})